import { Form, DatePicker, Button, Flex, notification, Select, SelectProps, AutoComplete, Tooltip, Tag } from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiMagnify, mdiMenuDown } from "@mdi/js";
import { endOfDay, subDays } from "date-fns";
import ListApi from "../../api-services/list-api.service";
import { monthFormat } from "../../utils/antd.props";
import dayjs from "dayjs";

const disabledDate= (current) => {
  // Can not select days before today
  const date = subDays(new Date(), 1) 
  return current && current < endOfDay(date)
};

const renderItem = ({id, name}) => ({
  key: id,
  value: name,
  label: name
});

function SearchBarWidget() {
  const listApi = new ListApi;
  const navigate= useNavigate();
  const [searchParams] =  useSearchParams();
  const [form] = Form.useForm();
  const [options, setOptions] = useState<SelectProps['options']>([]);
  const [brands, setBrands] = useState<SelectProps['options']>([]);
  const [exEnds, setExEnds] = useState<SelectProps['options']>([]);
  const initialFormValues = {};
    for (const [key, value] of searchParams.entries()) {
      if(key == "date"){
        initialFormValues["date"] = dayjs(value)
      }
      else if(key == "brands"){
        initialFormValues["brands"] = value ? value.split(",").filter(Boolean) : []
      }
      else{
        initialFormValues[key] = value;
      }
    }

  useEffect(() => {
      Promise.all([
        listApi.getLocations(),
        listApi.getItineraries(),
        listApi.getBrands(),
        listApi.getExEndOptions()
      ]).then(([locations, itineraries, brands, exEnds]) => {
        var options:any = [];
        if(locations.length > 0){
          options.push({
            label: <strong> Destinations </strong>,
            options: locations.map((location:any) => renderItem(location))
          })
        }
        if(itineraries.length > 0){
          options.push({
            label: <strong> Itineraries </strong>,
            options: itineraries.map((itinerary:any) => renderItem(itinerary))
          })
        }
        setOptions(options);
        setBrands(brands);
        setExEnds(exEnds);
      }).catch(error => {
        notification.error({ message: 'Something went wrong.'});
      });
  }, []);

  useEffect(() => {
    // 1. Check if we are exactly on the homepage
    if (location.pathname === '/web') {
      form.setFieldsValue({
        brands: ["GD_Standard"]
      });
    }
  }, [location.pathname, form]);


  const save = (formValues) => {
    let searchObj = {};

    Object.keys(formValues).forEach(filter => {
      const value = formValues[filter];
      
      if (value !== undefined && value !== null && (!Array.isArray(value) || value.length > 0)) {
      
        searchObj[filter] = filter === "date" && value?.format
          ? value.format("YYYY-MM-01")
          : value;
      }
    })
    
    searchObj["type"] = getBrandType(formValues.brands);
    
    const searchParams = new URLSearchParams(searchObj).toString();
    navigate({
      pathname: '/web/listing',
      search: `?${searchParams}`
    });
    navigate(0);
  }


  const getBrandType = (selectedBrands: string[]) => {
    if (!selectedBrands?.length || !brands) {
      return "series";
    }
    const selectedBrand = brands.find(b => b.options.some(opt => selectedBrands.includes(opt.value)));
    return selectedBrand ? selectedBrand.type : "series";
  }

  const selectedBrands = Form.useWatch("brands", form);
  const disableExEnd = React.useMemo(() => {
    if (!selectedBrands) return false;
    const cruiseBrandIds = brands?.find((group: any) => group.type === "cruise")?.options.map((opt: any) => opt.value) ?? [];
    const selectedValues = Array.isArray(selectedBrands) ? selectedBrands : [selectedBrands];
    return selectedValues.some((value) => cruiseBrandIds.includes(value));
  }, [selectedBrands, brands]);

  useEffect(() => {
    if (disableExEnd) {
      form.setFieldsValue({ ex_end: undefined });
    }
  }, [disableExEnd, form]);
  
  
  return (
    <div id="search-bar">
        <img src="/iceholidays-assets/images/Rectangle49.png"/>
        <div id="search-bar-widget">
          <Form
            onFinish={save}
            initialValues={initialFormValues}
            layout="vertical"
            form={form}
          >
            <Form.Item wrapperCol={{ span: 24 }} label="Brand" name="brands">
              <Select
                allowClear
                showSearch
                placeholder="Search Brand" suffixIcon={<Icon path={mdiMenuDown} size={1} />}
                options={brands}
                mode="multiple"
                maxTagCount="responsive"
                maxTagPlaceholder={(omittedValues) => (
                  <Tooltip
                    styles={{ root: { pointerEvents: 'none' } }}
                    title={omittedValues.map(({ label }) => label).join(', ')}
                  >
                    <span>+{omittedValues.length} ...</span>
                  </Tooltip>
                )}
              />
            </Form.Item>
            <Form.Item wrapperCol={{ span: 24 }} label="Departure From" name="ex_end" className={disableExEnd ? "disabled-form-item" : ""}>
              <Select
                allowClear
                showSearch
                placeholder="Search Departure From" suffixIcon={<Icon path={mdiMenuDown} size={1} />}
                options={exEnds}
                disabled={disableExEnd}
                optionFilterProp="searchText"
                optionRender={(option) => {
                  const data = option.data as { label: string; code?: string };

                  return (
                    <Flex justify="space-between" align="center" gap={12}>
                      <span>{data.label}</span>
                      {data.code && <Tag>{data.code}</Tag>}
                    </Flex>
                  );
                }}
              />
            </Form.Item>
            <Form.Item wrapperCol={{ span: 24 }} label="Keywords" name="keyword">
              <AutoComplete
                showSearch
                allowClear
                filterOption
                placeholder="Destination" suffixIcon={<Icon path={mdiMenuDown} size={1} />}
                options={options}
              />
            </Form.Item>
            <Form.Item wrapperCol={{ span: 24 }} label="Travel Period" name="date" className="date-filter">
              <DatePicker picker="month" format={monthFormat} inputReadOnly placeholder="Select Date" disabledDate={disabledDate}/>
            </Form.Item>
            <Flex align="flex-end" justify="center">
              <Button type="primary" className="search-button" htmlType="submit" icon={<Icon path={mdiMagnify} size={1} />}> Search </Button>
            </Flex>
          </Form>
        </div>
        <img src="/iceholidays-assets/images/Rectangle49.png" className="flip"/>
      </div>
  )
}

export default SearchBarWidget;
